import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["backButton", "backLabel", "cancelButton", "cancelLabel", "doneButton", "doneLabel", "hidePrimaryControls", "hideSecondaryControls", "isFirstStep", "isLastStep", "nextButton", "nextLabel", "onClickBack", "onClickCancel", "onClickDone", "onClickNext", "stepIndex", "stepProps", "wizardProps"];
import { useContext } from 'react';
import * as CustomRenderer from '../AbstractPageTemplate/customRenderer';
import { DialogFooter } from '../DialogFooter';
import { ButtonWrapper } from '../ButtonWrapper/ButtonWrapper';
import { Tooltip } from '../Tooltip/Tooltip';
import { DialogContext } from '../Dialog/DialogContext';
import { filterProps } from '../../../utils/filterProps';
import { useTheme } from 'foundations-theming/utils';
import { WizardFooterProvider } from './WizardFooterProvider';
import { LeftIcon } from 'foundations-assets/icons/components/LeftIcon';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { PanelContext } from '../../context/PanelContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const renderCancelButton = ({
  cancelButton,
  cancelLabel,
  onClickCancel
}) => CustomRenderer.render(cancelButton, {
  children: cancelLabel,
  'data-action': 'close',
  key: 'cancel',
  onClick: onClickCancel,
  use: 'link',
  'data-wizard-action-name': 'cancel'
});
const renderBackButton = ({
  backButton,
  backLabel,
  onClickBack,
  wizardProps
}) => {
  if (!wizardProps.backable) {
    return null;
  }
  return CustomRenderer.render(backButton, {
    children: [/*#__PURE__*/_jsx(LeftIcon, {}, "icon"), backLabel],
    key: 'back',
    onClick: onClickBack,
    use: 'tertiary-light',
    'data-wizard-action-name': 'back'
  });
};
const renderNextButton = ({
  nextButton,
  nextLabel,
  onClickNext,
  wizardProps
}) => {
  return CustomRenderer.render(nextButton, {
    children: [nextLabel, /*#__PURE__*/_jsx(RightIcon, {}, "icon")],
    disabled: wizardProps.disablePrimaryButton,
    onClick: onClickNext,
    use: 'primary',
    'data-wizard-action-name': 'next'
  });
};
const renderDoneButton = ({
  doneButton,
  doneLabel,
  onClickDone,
  wizardProps
}) => {
  return CustomRenderer.render(doneButton, {
    children: doneLabel,
    disabled: wizardProps.disablePrimaryButton,
    onClick: onClickDone,
    use: 'primary',
    'data-wizard-action-name': 'done'
  });
};
const renderPrimaryButton = ({
  isLastStep,
  wizardProps,
  renderedDoneButton,
  renderedNextButton
}) => {
  const renderedButton = isLastStep ? renderedDoneButton : renderedNextButton;
  const {
    primaryButtonTooltip,
    primaryButtonTooltipProps
  } = wizardProps;
  return primaryButtonTooltip ? /*#__PURE__*/_jsx(Tooltip, Object.assign({
    title: primaryButtonTooltip
  }, primaryButtonTooltipProps, {
    children: renderedButton
  })) : renderedButton;
};
const renderSecondaryButtons = ({
  isFirstStep,
  wizardProps,
  renderedBackButton,
  renderedCancelButton
}) => isFirstStep ? renderedCancelButton : [renderedBackButton, wizardProps.cancellable ? renderedCancelButton : null];
export const WizardFooter = _ref => {
  let {
      backButton,
      backLabel,
      cancelButton,
      cancelLabel,
      doneButton,
      doneLabel,
      hidePrimaryControls = false,
      hideSecondaryControls = false,
      isFirstStep,
      isLastStep,
      nextButton,
      nextLabel,
      onClickBack,
      onClickCancel,
      onClickDone,
      onClickNext,
      wizardProps
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    tokenProps,
    domProps
  } = filterProps(rest);
  const theme = useTheme();
  const dialogContext = useContext(DialogContext);
  const wizardFooterStyleOverrides = {
    paddingBlockStart: theme.space['400'],
    paddingBlockEnd: theme.space['400'],
    backgroundColor: theme.color['fill-surface-raised'],
    borderBlockStart: `${theme.borderWidth['100']} solid ${theme.color['border-core-default']}`
  };
  const renderedCancelButton = dialogContext.use === 'fullscreen' ? renderCancelButton({
    cancelButton,
    cancelLabel,
    onClickCancel
  }) : null;
  const renderedBackButton = renderBackButton({
    backButton,
    backLabel,
    onClickBack,
    wizardProps
  });
  const renderedNextButton = renderNextButton({
    nextButton,
    nextLabel,
    onClickNext,
    wizardProps
  });
  const renderedDoneButton = renderDoneButton({
    doneButton,
    doneLabel,
    onClickDone,
    wizardProps
  });
  return /*#__PURE__*/_jsx(WizardFooterProvider, {
    footerStyleOverrides: Object.assign({}, wizardFooterStyleOverrides),
    children: /*#__PURE__*/_jsx(PanelContext.Provider, {
      value: {
        inPanel: true,
        inModal: false
      },
      children: /*#__PURE__*/_jsxs(DialogFooter, Object.assign({
        align: "between"
      }, tokenProps, domProps, {
        children: [/*#__PURE__*/_jsx(ButtonWrapper, {
          children: hideSecondaryControls ? '' : renderSecondaryButtons({
            isFirstStep,
            wizardProps,
            renderedBackButton,
            renderedCancelButton
          })
        }), hidePrimaryControls ? '' : renderPrimaryButton({
          isLastStep,
          wizardProps,
          renderedDoneButton,
          renderedNextButton
        })]
      }))
    })
  });
};
WizardFooter.displayName = 'WizardFooter';